<template>
  <v-dialog v-model="internalDialog" max-width="1100px" scrollable persistent transition="dialog-bottom-transition">
    <v-card class="rounded-lg overflow-hidden bg-white">
      <!-- HEADER -->
      <v-card-title class="d-flex justify-space-between align-center py-4 px-6 primary darken-2 white--text">
        <div class="d-flex align-center">
          <v-avatar color="white" size="40" class="me-3">
            <v-icon color="primary darken-2" size="24">mdi-calculator-variant</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-black white--text mb-0">تقرير صافي أرباح الشركة</div>
            <div class="text-caption white--text text--lighten-3 opacity-90">
              تحليل دقيق ومفصل لكافة الإيرادات، مستحقات السائقين، والمبالغ المستردة
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-btn
            color="success darken-1"
            dark
            class="font-weight-bold elevation-2 me-3 text-none"
            :loading="loadingCsv"
            @click="exportCsv"
          >
            <v-icon left>mdi-file-delimited</v-icon>
            تصدير CSV
          </v-btn>

          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <!-- BODY -->
      <v-card-text class="pa-5 bg-grey-lighten-5">
        <!-- Alert Banner if Error -->
        <v-alert v-if="errorMessage" type="error" dense class="mb-4 font-weight-bold d-flex align-center justify-space-between">
          <div>{{ errorMessage }}</div>
          <v-btn x-small color="white" text class="font-weight-bold ms-3" @click="fetchReport">إعادة المحاولة</v-btn>
        </v-alert>

        <!-- 1. FILTERS BAR -->
        <v-card outlined class="pa-4 mb-5 rounded-lg border-grey bg-white elevation-1">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">الفترة الزمنية:</div>
              <v-select
                v-model="selectedPeriod"
                :items="periodOptions"
                item-text="text"
                item-value="value"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="onPeriodChange"
              ></v-select>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">من تاريخ:</div>
              <v-text-field
                v-model="dateFrom"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>

            <v-col v-if="selectedPeriod === 'custom'" cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">إلى تاريخ:</div>
              <v-text-field
                v-model="dateTo"
                type="date"
                dense
                outlined
                hide-details
                class="bg-white"
                @change="fetchReport"
              ></v-text-field>
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <div class="text-caption font-weight-bold text-dark-secondary mb-1">بحث برقم الحجز / اسم السائق / العميل:</div>
              <v-text-field
                v-model="searchQuery"
                placeholder="ابحث..."
                dense
                outlined
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
                class="bg-white"
                @keyup.enter="fetchReport"
                @click:clear="onClearSearch"
              ></v-text-field>
            </v-col>
          </v-row>
        </v-card>

        <!-- 2. RECONCILED KPI SUMMARY CARDS -->
        <v-row dense class="mb-5">
          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-4 rounded-lg border-primary bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي الإيرادات المحصلة (Gross)</div>
              <div class="text-h5 font-weight-black text-dark-primary mt-1">
                {{ summary.total_booking_revenue || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">كافة الحجوزات المدفوعة قبل الاسترداد</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-card outlined class="pa-4 rounded-lg border-warning bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي المبالغ المستردة</div>
              <div class="text-h5 font-weight-black warning--text text--darken-3 mt-1">
                {{ summary.total_refunds_amount || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">أموال الإلغاءات المردودة للعملاء</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-card outlined class="pa-4 rounded-lg border-teal bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">صافي الإيرادات المحتفظ بها</div>
              <div class="text-h5 font-weight-black teal--text text--darken-2 mt-1">
                {{ summary.retained_revenue || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">الإيرادات المحصلة - المستردات</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="2">
            <v-card outlined class="pa-4 rounded-lg border-info bg-white elevation-1">
              <div class="text-caption font-weight-bold text-dark-secondary">إجمالي مستحقات السائقين</div>
              <div class="text-h5 font-weight-black info--text text--darken-2 mt-1">
                {{ summary.total_driver_earnings || '0.00' }} <span class="text-caption">EGP</span>
              </div>
              <div class="text-caption text-dark-secondary mt-1">أرباح السائقين من الرحلات</div>
            </v-card>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-card outlined class="pa-4 rounded-lg border-success bg-light-green elevation-2">
              <div class="text-caption font-weight-bold green--text text--darken-4">صافي أرباح الشركة المتبقية</div>
              <div class="text-h4 font-weight-black green--text text--darken-3 mt-1">
                {{ summary.company_net_profit || '0.00' }} <span class="text-caption font-weight-bold">EGP</span>
              </div>
              <div class="text-caption green--text text--darken-4 font-weight-bold mt-1">
                الإيرادات المحتفظ بها - أرباح السائقين
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- 3. DETAILED TRANSACTIONS TABLE -->
        <v-card outlined class="rounded-lg border-grey bg-white elevation-1">
          <v-card-title class="pa-3 grey lighten-4 border-bottom d-flex align-center justify-space-between">
            <span class="text-subtitle-1 font-weight-bold text-dark-primary">
              <v-icon size="20" color="primary" class="me-1">mdi-table-large</v-icon>
              جدول التفاصيل والحركات ({{ table.total }} عملية)
            </span>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="table.data"
            :loading="loading"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.ticket_number="{ item }">
              <span class="font-weight-bold primary--text">{{ item.ticket_number }}</span>
            </template>

            <template v-slot:item.gross_amount="{ item }">
              <span class="font-weight-bold text-dark-primary">{{ numberFormat(item.gross_amount) }} EGP</span>
            </template>

            <template v-slot:item.driver_share="{ item }">
              <span class="font-weight-bold info--text text--darken-2">{{ numberFormat(item.driver_share) }} EGP</span>
            </template>

            <template v-slot:item.refunded_amount="{ item }">
              <span class="font-weight-bold warning--text text--darken-3">{{ numberFormat(item.refunded_amount) }} EGP</span>
            </template>

            <template v-slot:item.net_company_profit="{ item }">
              <span class="font-weight-black green--text text--darken-3">{{ numberFormat(item.net_company_profit) }} EGP</span>
            </template>

            <template v-slot:no-data>
              <div class="text-center py-6 text-dark-secondary">
                <v-icon size="40" color="grey lighten-1">mdi-file-hidden</v-icon>
                <div class="mt-2 font-weight-bold">لا توجد عمليات مطابقة للفترة المحددة</div>
              </div>
            </template>
          </v-data-table>

          <!-- PAGINATION -->
          <div class="pa-3 border-top d-flex justify-space-between align-center flex-wrap gap-2">
            <div class="text-caption text-dark-secondary font-weight-bold">
              عرض الصفحة {{ table.page }} من {{ table.last_page }} (إجمالي {{ table.total }} سجل)
            </div>
            <v-pagination
              v-if="table.last_page > 1"
              v-model="table.page"
              :length="table.last_page"
              total-visible="7"
              dense
              @input="fetchReport"
            ></v-pagination>
          </div>
        </v-card>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 bg-white d-flex justify-space-between align-center">
        <div class="caption font-weight-bold text-dark-secondary">
          BEEPGO Accounting — الأرقام محسوبة تلقائياً وبشكل محدد
        </div>
        <v-btn color="grey darken-2" text class="font-weight-bold" @click="closeModal">إغلاق</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  name: 'CompanyNetProfitModal',
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      internalDialog: false,
      loading: false,
      loadingCsv: false,
      errorMessage: null,

      selectedPeriod: 'month',
      dateFrom: null,
      dateTo: null,
      searchQuery: '',

      periodOptions: [
        { text: 'اليوم (Today)', value: 'today' },
        { text: 'هذا الأسبوع (This Week)', value: 'week' },
        { text: 'هذا الشهر (This Month)', value: 'month' },
        { text: 'هذه السنة (This Year)', value: 'year' },
        { text: 'آخر 7 أيام', value: 'last_7_days' },
        { text: 'آخر 30 يوم', value: 'last_30_days' },
        { text: 'فترة مخصصة (Custom)', value: 'custom' },
        { text: 'الكل (All Time)', value: 'all' },
      ],

      summary: {
        total_booking_revenue: '0.00',
        total_driver_earnings: '0.00',
        total_refunds_amount: '0.00',
        company_net_profit: '0.00',
      },

      table: {
        data: [],
        total: 0,
        page: 1,
        per_page: 15,
        last_page: 1,
      },

      headers: [
        { text: 'رقم التذكرة', value: 'ticket_number' },
        { text: 'المسار', value: 'route_name' },
        { text: 'العميل', value: 'customer_name' },
        { text: 'الهاتف', value: 'customer_phone' },
        { text: 'السائق', value: 'driver_name' },
        { text: 'طريقة الدفع', value: 'payment_method' },
        { text: 'المبلغ المحصل', value: 'gross_amount' },
        { text: 'حصة السائق', value: 'driver_share' },
        { text: 'صافي ربح الشركة', value: 'net_company_profit' },
        { text: 'التاريخ', value: 'date' },
      ],
    };
  },
  watch: {
    value(val) {
      this.internalDialog = val;
      if (val) {
        this.fetchReport();
      }
    },
    internalDialog(val) {
      this.$emit('input', val);
    },
  },
  methods: {
    numberFormat(val) {
      return (parseFloat(val) || 0).toFixed(2);
    },
    onPeriodChange() {
      this.table.page = 1;
      this.fetchReport();
    },
    onClearSearch() {
      this.searchQuery = '';
      this.table.page = 1;
      this.fetchReport();
    },
    fetchReport() {
      this.loading = true;
      this.errorMessage = null;

      const params = {
        period: this.selectedPeriod,
        search: this.searchQuery,
        page: this.table.page,
        per_page: this.table.per_page,
      };

      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/company-net-profit', { params })
        .then((res) => {
          if (res.data && res.data.success) {
            const data = res.data.data;
            this.summary = data.summary || this.summary;
            this.table = data.table || this.table;
          } else {
            this.errorMessage = res.data.message || 'خطأ في جلب التقرير';
          }
        })
        .catch((err) => {
          this.errorMessage = 'تعذر الاتصال بالخادم لجلب التقرير المالي';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    exportCsv() {
      this.loadingCsv = true;
      const params = {
        period: this.selectedPeriod,
        search: this.searchQuery,
      };
      if (this.selectedPeriod === 'custom') {
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
      }

      axios
        .get('/reports/financial/company-net-profit/export-csv', {
          params,
          responseType: 'blob',
        })
        .then((res) => {
          const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `company-profit-${moment().format('YYYY-MM-DD')}.csv`;
          link.click();
          window.URL.revokeObjectURL(link.href);
        })
        .catch((err) => {
          console.error('Error exporting CSV:', err);
        })
        .finally(() => {
          this.loadingCsv = false;
        });
    },
    closeModal() {
      this.internalDialog = false;
    },
  },
};
</script>

<style scoped>
.bg-light-green {
  background-color: #F0FDF4 !important;
  border-color: #86EFAC !important;
}
</style>
